/**
 * Font-size and spacing scales for TS/TSX — the mirror of
 * src/variables/_sizes.scss and src/variables/_spacing.scss (same px keys).
 *
 *   fontSize: fs(14)        → "14px"     (CSS string: style={{}}, template strings)
 *   fontSize: fsPx(20)      → 20         (number — MUI sx / React read it as px)
 *   fontSize: fsRem(16)     → "1rem"     (key = px at the 16px root)
 *   padding:  sp(8, 16)     → "8px 16px" (px, any count; negatives allowed: sp(0, -4))
 *
 * The argument types are the scale's keys, so a size that is not on the scale
 * is a compile error — add it to the scale (SCSS and here) instead of typing a
 * literal. Numeric MUI spacing (`sx={{ p: 2 }}`) is already a theme token and
 * is written as before.
 *
 * GENERATED by the design-token refactor (docs/knowledge/design-tokens.md), then
 * maintained by hand.
 */

export const FONT_SIZE_PX = {
  5: 5,
  "6.6": 6.6,
  "7.5": 7.5,
  8: 8,
  "8.3": 8.3,
  9: 9,
  "9.3": 9.3,
  10: 10,
  11: 11,
  "11.21": 11.21,
  "11.6": 11.6,
  "11.63": 11.63,
  12: 12,
  "12.06": 12.06,
  "12.4": 12.4,
  "12.5": 12.5,
  "12.6": 12.6,
  13: 13,
  14: 14,
  "14.1": 14.1,
  "14.544": 14.544,
  15: 15,
  "15.52": 15.52,
  16: 16,
  "16.4": 16.4,
  17: 17,
  18: 18,
  "18.7": 18.7,
  "19.39": 19.39,
  20: 20,
  22: 22,
  24: 24,
  26: 26,
  28: 28,
  32: 32,
  40: 40,
  44: 44,
  48: 48,
  56: 56,
  74: 74,
} as const;

export const FONT_SIZE_REM = {
  8: "0.5rem",
  "9.6": "0.6rem",
  "12.8": "0.8rem",
  "13.6": "0.85rem",
  "14.4": "0.9rem",
  "15.2": "0.95rem",
  16: "1rem",
  "17.6": "1.1rem",
  20: "1.25rem",
  24: "1.5rem",
} as const;

export const SPACE_PX = [0.5, 1, 1.1, 1.5, 1.8, 2, 2.5, 3, 3.1, 3.11, 3.5, 3.68, 4, 4.12, 4.5, 5, 6, 6.2, 6.23, 6.3, 6.5, 6.6667, 6.8, 7, 7.2, 7.5, 7.55, 7.8, 7.9, 8, 8.56, 8.72, 8.87, 9, 9.1, 9.2, 9.3, 10, 10.37, 10.5, 11, 11.26, 12, 12.23, 12.3, 12.45, 12.5, 12.7, 12.77, 13, 13.3, 13.5, 14, 14.3, 14.33, 15, 16, 16.2, 17, 18, 19, 20, 20.2, 20.5, 21, 21.28, 22, 22.75, 23, 23.7, 24, 24.25, 25, 25.22, 25.3, 25.75, 26, 26.5, 27, 28, 28.28, 28.5, 28.75, 29, 29.36, 30, 30.13, 31, 31.77, 32, 33, 33.3, 33.33, 34, 34.26, 35, 36, 37.38, 38, 38.5, 38.9, 40, 42, 45, 48, 50, 52, 55, 56, 57, 60, 64, 65, 70, 72, 74, 75, 80, 90, 91, 92, 95, 100, 104, 110, 114, 115, 116, 121, 140, 150, 156, 170, 200, 230, 350] as const;

/** Mirror of the rem spacing tokens in _spacing.scss (rem follows the browser font size, so these stay rem). */
export const SPACE_REM = ["0.2rem", "0.25rem", "0.3rem", "0.35rem", "0.4rem", "0.45rem", "0.5rem", "0.6rem", "0.7rem", "0.8rem", "0.9rem", "1rem", "1.2rem", "1.4rem", "1.5rem", "2rem", "2.5rem", "3rem", "4.5rem"] as const;

/** Mirror of the relative spacing tokens in _spacing.scss (% of the containing block, em of the font size). */
export const SPACE_RELATIVE = ["0.15em", "1.5em", "4%", "5%", "6%", "8%", "10%", "16%", "18%", "35%"] as const;

/** Mirror of the stroke tokens in _layout.scss ($border-width, $border-width-selected, $border-width-<n>). */
export const BORDER_WIDTH_PX = [0.1, 0.31, 0.38, 0.41, 0.5, 0.51, 0.62, 0.66, 0.99, 1, 1.1, 1.2, 1.4, 1.5, 1.7, 2, 3, 4, 5, 7, 8, 10] as const;
/** Border width as a CSS px string: `bw(1)` → `"1px"`. */
export const bw = (px: (typeof BORDER_WIDTH_PX)[number]): string => `${px}px`;

/** Mirror of the px tokens in src/variables/_border-radius.scss. */
export const RADIUS_PX = [1, 1.1, 1.8, 2, 2.5, 2.6, 2.66, 3, 3.73, 3.75, 3.8, 4, 4.8, 4.9, 5, 6, 6.5, 7, 7.5, 8, 9, 9.6, 10, 11, 12, 14, 15, 16, 18, 19, 20, 21, 22, 24, 25, 26, 30, 32, 33, 33.33, 40, 46.5, 50, 90, 95, 100, 200, 999, 9999] as const;

type FontPx = keyof typeof FONT_SIZE_PX;
type FontPxArg = FontPx extends infer K ? (K extends `${infer N extends number}` ? N : K) : never;
type FontRemArg = keyof typeof FONT_SIZE_REM extends infer K ? (K extends `${infer N extends number}` ? N : K) : never;
type SpacePx = (typeof SPACE_PX)[number];
/** A spacing value: 0, a scale step, or a negative scale step. */
export type SpaceValue = 0 | SpacePx | NegativeOf<SpacePx>;
type NegativeOf<T extends number> = T extends number ? (`-${T}` extends `${infer N extends number}` ? N : never) : never;

/** Font size as a CSS px string: `fs(14)` → `"14px"`. */
export const fs = (px: FontPxArg): string => `${FONT_SIZE_PX[px as FontPx]}px`;
/** Font size as a number (px): `fsPx(20)` → `20`. */
export const fsPx = (px: FontPxArg): number => FONT_SIZE_PX[px as FontPx];
/** Font size in rem, keyed by px at the 16px root: `fsRem(16)` → `"1rem"`. */
export const fsRem = (px: FontRemArg): string => FONT_SIZE_REM[px as keyof typeof FONT_SIZE_REM];
/** Spacing as a CSS string: `sp(8)` → `"8px"`, `sp(8, 16)` → `"8px 16px"`, `sp(0, -4)` → `"0 -4px"`. */
export const sp = (...px: SpaceValue[]): string => px.map((v) => (v === 0 ? "0" : `${v}px`)).join(" ");

/** A border-radius value: 0 or a radius-scale step. */
export type RadiusValue = 0 | (typeof RADIUS_PX)[number];
/** Border radius as a CSS string: `br(8)` → `"8px"`, `br(10, 10, 0, 0)` → `"10px 10px 0 0"`. */
export const br = (...px: RadiusValue[]): string => px.map((v) => (v === 0 ? "0" : `${v}px`)).join(" ");

/** Mirror of the $font-weight-* tokens (_font_family.scss). */
export const FONT_WEIGHT = [300, 400, 500, 600, 700, 800, 900] as const;
/** Mirror of the $z-index-* tokens (_z-index.scss). */
export const Z_INDEX = [-1, 1, 2, 3, 4, 5, 7, 9, 10, 11, 12, 20, 22, 30, 50, 55, 60, 90, 99, 100, 101, 102, 998, 999, 1000, 1001, 1002, 1200, 1250, 1260, 1400, 1500, 1501, 2000, 9998, 9999, 10000, 99999, 100000, 100001, 999999, 2147483646, 2147483647] as const;
/** Mirrors of the $line-height-* tokens (_line-height.scss). */
export const LINE_HEIGHT_RATIO = [1, 1.1, 1.2, 1.25, 1.3, 1.35, 1.4, 1.45, 1.5, 1.55, 1.6] as const;
export const LINE_HEIGHT_PX = [5.4, 7.47, 7.5, 8, 9, 9.35, 10, 10.44, 10.6, 11, 11.2, 12, 12.4, 13, 13.5, 13.7, 14, 14.6, 15, 15.24, 15.57, 15.6, 16, 16.1, 17, 18, 18.57, 19, 20, 21, 21.66, 22, 22.4, 23, 24, 29, 35, 36, 40, 44, 50, 54] as const;
export const LINE_HEIGHT_PCT = [120, 130, 140] as const;

/** Font weight on the token scale: `fw(600)` → `600`. */
export const fw = (w: (typeof FONT_WEIGHT)[number]): number => w;
/** z-index on the token scale: `zi(10)` → `10`. */
export const zi = (z: (typeof Z_INDEX)[number]): number => z;
/** Unitless line-height ratio: `lh(1.2)` → `1.2`. */
export const lh = (r: (typeof LINE_HEIGHT_RATIO)[number]): number => r;
/** px line-height: `lhPx(12)` → `"12px"`. */
export const lhPx = (px: (typeof LINE_HEIGHT_PX)[number]): string => `${px}px`;
/** percent line-height: `lhPct(140)` → `"140%"`. */
export const lhPct = (p: (typeof LINE_HEIGHT_PCT)[number]): string => `${p}%`;
